// src/components/FilterForms.jsx

import { Form, Row, Col, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./FilterForm.css"; // We'll define some mobile-friendly overrides here
import { useGetCountySalesQuery } from "../../redux/countySlice";

/**
 * Filter dropdowns for State, County, Sale Type, and Time Frame.
 *
 * Note: sale type clearing on state/county change is intentionally handled
 * in Properties.js (handleStateChange / handleCountyChange) rather than here.
 * A previous resetSaleType effect in this component was removed because it
 * fired before the county sales query resolved on deep-linked page loads,
 * wiping out the sale type that had just been dispatched from the URL params.
 */
const FilterForm = ({
  states,
  selectedState,
  handleStateChange,
  counties,
  selectedCounty,
  setSelectedCounty,
  saleTypes,
  selectedSaleType,
  setSelectedSaleType,
  timeFrames,
  selectedTimeFrame,
  handleTimeFrameChange,
  resetDropdowns,
}) => {
  const { data, isLoading, error } = useGetCountySalesQuery(
    { stateCode: selectedState, countyName: selectedCounty },
    { skip: !selectedState || !selectedCounty }
  );
  const navigate = useNavigate();

  const handleCountyInfoClick = () => {
    navigate(`/county/${selectedState}/${selectedCounty}`);
  };

  const numberOfSalesPerSaleType = data
    ? data.reduce((acc, sale) => {
        const saleType = sale.SaleType;
        acc[saleType] = (acc[saleType] || 0) + 1;
        return acc;
      }, {})
    : {};

  const shouldDisplayCounts = selectedCounty && selectedState && !isLoading && !error;

  const displayAvailableSaleTypes = () => {
    if (data && data.length > 0) {
      return (
        <>
          <option value="">Select a Sale Type</option>
          {saleTypes.map((type, index) => {
            const saleCount = numberOfSalesPerSaleType[type.id];
            if (saleCount > 0) {
              const displayName = data.find((sale) => sale.SaleType === type.id)?.name || type.name;
              return (
                <option key={index} value={type.id}>
                  {displayName} {shouldDisplayCounts ? `(${saleCount})` : null}
                </option>
              );
            }
            return null;
          })}
        </>
      );
    }
    return <option value="">No Active Listings At This Time</option>;
  };

  return (
    <div className="filter-form-container" role="search" aria-label="Property filters">
      <Form>
        <Row className="filter-form-row">
          {/* State */}
          <Col xs={12} sm={6} md={6} lg={3}>
            <Form.Group controlId="stateSelect">
              <Form.Label className="label-bg">Select State</Form.Label>
              <Form.Control as="select" value={selectedState} onChange={handleStateChange}>
                <option value="">Select a State</option>
                {states.map((state, index) => (
                  <option key={index} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>

          {/* County */}
          <Col xs={12} sm={6} md={6} lg={3}>
            <Form.Group controlId="countySelect">
              <Form.Label className="label-bg">Select County</Form.Label>
              <Form.Control
                as="select"
                value={selectedCounty}
                onChange={(e) => setSelectedCounty(e.target.value)}
                disabled={!selectedState}
              >
                <option value="">Select a County</option>
                {counties.map((county, index) => (
                  <option key={index} value={county.id}>
                    {county.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>

          {/* Sale Type */}
          <Col xs={12} sm={6} md={6} lg={3}>
            <Form.Group controlId="saleTypeSelect">
              <Form.Label className="label-bg">All Sale Types</Form.Label>
              <Form.Control
                as="select"
                value={selectedSaleType}
                onChange={(e) => setSelectedSaleType(e.target.value)}
                disabled={!shouldDisplayCounts}
              >
                {!data && !selectedCounty ? <option value="">Select a Sale Type</option> : displayAvailableSaleTypes()}
              </Form.Control>
            </Form.Group>
          </Col>

          {/* Time Frame */}
          <Col xs={12} sm={6} md={6} lg={3}>
            <Form.Group controlId="timeFrameSelect">
              <Form.Label className="label-bg">Select Time Frame</Form.Label>
              <Form.Control as="select" value={selectedTimeFrame} onChange={handleTimeFrameChange}>
                {timeFrames.map((timeFrame, index) => (
                  <option key={index} value={timeFrame}>
                    {timeFrame}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

        <Row className="button-row">
          <Col xs={6} className="text-center">
            <Button variant="outline-secondary" className="resetBtn" onClick={resetDropdowns}>
              Reset Filters
            </Button>
          </Col>
          <Col xs={6} className="text-center">
            <Button
              variant="outline-secondary"
              className="w-75 countyBtn align-items-center"
              onClick={handleCountyInfoClick}
              disabled={!selectedCounty}
            >
              County Information <span className="badge rounded-pill newBadge">New</span>
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default FilterForm;
